'use client'

import React, {useMemo} from 'react'

/**
 * @typedef {{
 * addressInformation: {
 *      firstName: string,
 *      lastName: string,
 *      address: string,
 *      city: string,
 *      postalCode: string,
 *      state: string,
 *      phone?: string,
 *      company?: string,
 *      accountName?: string,
 * },
 * isValidAddress: boolean,
 * updateAddressField: (field: string, value: string) => void,
 * updateFullAddress: (address: object) => void,
 * resetAddress: () => void,
 * setFromBusinessAddress: (businessAddress: object) => void,
 * }} PaymentAddressContext
 */

const PaymentAddressContext = React.createContext(null)

const initialAddressState = {
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  postalCode: '',
  state: '',
  phone: '',
  company: '',
  accountName: '',
}

export function PaymentAddressProvider({children}) {
  const [addressInformation, setAddressInformation] =
    React.useState(initialAddressState)

  const updateAddressField = React.useCallback((field, value) => {
    setAddressInformation(prevState => ({
      ...prevState,
      [field]: value,
    }))
  }, [])

  const updateFullAddress = React.useCallback(address => {
    setAddressInformation(prevState => ({
      ...prevState,
      ...address,
    }))
  }, [])

  const resetAddress = React.useCallback(() => {
    setAddressInformation(initialAddressState)
  }, [])

  const setFromBusinessAddress = React.useCallback(businessAddress => {
    if (!businessAddress) return

    setAddressInformation(prevState => {
      const next = {...prevState}

      Object.entries(businessAddress).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          if ((key === 'firstName' || key === 'lastName') && prevState[key]) {
            return
          }
          next[key] = value
        }
      })

      const postalCode = businessAddress.postalCode || businessAddress.postCode
      if (postalCode) {
        next.postalCode = postalCode
      }

      const phone = businessAddress.phone || businessAddress.companyPhone
      if (phone) {
        next.phone = phone
      }

      const accountName =
        businessAddress.firstName && businessAddress.lastName
          ? `${businessAddress.firstName} ${businessAddress.lastName}`
          : businessAddress.accountName
      if (accountName) {
        next.accountName = accountName
      }

      return next
    })
  }, [])

  const isValidAddress = useMemo(() => {
    return Boolean(
      addressInformation.firstName &&
      addressInformation.lastName &&
      addressInformation.address &&
      addressInformation.city &&
      addressInformation.postalCode &&
      addressInformation.state,
    )
  }, [addressInformation])

  const value = React.useMemo(
    () => ({
      addressInformation,
      isValidAddress,
      updateAddressField,
      updateFullAddress,
      resetAddress,
      setFromBusinessAddress,
    }),
    [
      addressInformation,
      isValidAddress,
      updateAddressField,
      updateFullAddress,
      resetAddress,
      setFromBusinessAddress,
    ],
  )
  return (
    <PaymentAddressContext.Provider value={value}>
      {children}
    </PaymentAddressContext.Provider>
  )
}

/**
 * @returns {PaymentAddressContext}
 */
export function usePaymentAddress() {
  const context = React.useContext(PaymentAddressContext)
  if (!context) {
    throw new Error(
      "'usePaymentAddress' can't be called outside 'PaymentAddressProvider'",
    )
  }
  return context
}
